import { useEffect, useRef } from 'react';

import { cn } from '../lib/cn.js';
import {
  approach,
  drawTechFrame,
  LABEL_FONT,
  rgba,
  toRgb,
  type FrameRect,
  type Rgb,
} from './techFrame.js';

/**
 * Palavra desenhada em canvas que, sob o ponteiro, vira traço de vetor.
 *
 * Adaptado do "Tech Text" do reactbits para o nome do sistema — no trilho e,
 * grande, no alto das telas de credencial. A
 * letra mais próxima do ponteiro troca o preenchimento pelo contorno
 * tracejado e ganha a moldura de seleção da marca (`techFrame.ts`): quadro
 * com cantoneiras, faíscas correndo pela borda e o rótulo com as dimensões —
 * a estética de ferramenta de desenho, que é o que um instrumento de
 * engenharia tem a ver com a sua própria marca. Sem ninguém mexer, uma lente
 * imaginária varre a palavra devagar.
 *
 * O que ficou de fora da referência, de propósito:
 *
 * - **arrastar as letras.** A palavra é um link para o início; um gesto que
 *   começa como arrasto e termina como clique mandaria a pessoa para a tela
 *   inicial sem ela ter pedido.
 * - **o modo de revelação por área.** Numa palavra de 32px a lente cobre tudo;
 *   só o modo por letra tem leitura nesse tamanho.
 *
 * E o que foi acrescentado: a largura é a da própria palavra (o canvas se
 * dimensiona pelo texto medido, não pelo contêiner), as cores vêm do CSS em
 * vigor — `color` do contêiner, que no trilho é `rail-ink` —, e a fonte é a
 * do sistema, carregada pela Font Loading API antes do primeiro desenho.
 *
 * Três desligamentos: `prefers-reduced-motion` para a varredura (o ponteiro de
 * verdade continua funcionando), aba ou trilho escondidos para o laço, e
 * `aria-label` com o texto no lugar do canvas.
 */

/** Folga lateral para o quadro, as faíscas e o rótulo não saírem do canvas. */
const PAD_X = 22;

interface Sprite {
  image: HTMLCanvasElement;
  left: number;
  top: number;
}

interface Glyph {
  char: string;
  x: number;
  box: FrameRect;
  /** 0 = preenchida, 1 = só o contorno tracejado. */
  outline: number;
  fill: Sprite;
  dashes: Sprite;
}

interface Layout {
  size: number;
  baseline: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export function TechText({
  text,
  fontSize = 22,
  fontWeight = 600,
  letterSpacing = -0.02,
  strokeWidth = 1,
  dashLength = 3,
  dashGap = 2,
  specks = 12,
  labels = true,
  sweep = true,
  speed = 1,
  accent = 'ink',
  className,
}: {
  text: string;
  /** Em px. O canvas se dimensiona pela palavra medida nesta fonte. */
  fontSize?: number | undefined;
  fontWeight?: number | undefined;
  /** Em `em`, como o `letter-spacing` dos títulos. */
  letterSpacing?: number | undefined;
  strokeWidth?: number | undefined;
  dashLength?: number | undefined;
  dashGap?: number | undefined;
  /** Faíscas em volta do quadro. Zero desliga. */
  specks?: number | undefined;
  /** Rótulo "O 14 × 16" acima do quadro. */
  labels?: boolean | undefined;
  /** Varredura automática quando o ponteiro está fora. */
  sweep?: boolean | undefined;
  speed?: number | undefined;
  /**
   * Cor do quadro, das cantoneiras, das faíscas e do rótulo — o aparato da
   * lente. A letra tracejada fica na cor do texto, como na referência. `ink`
   * é a cor do texto; `primary` lê o token da marca.
   */
  accent?: 'ink' | 'primary' | undefined;
  className?: string | undefined;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const settings = {
    text,
    fontSize,
    fontWeight,
    letterSpacing,
    strokeWidth,
    dashLength,
    dashGap,
    specks,
    labels,
    sweep,
    speed,
    accent,
  };
  const settingsRef = useRef(settings);
  const wakeRef = useRef<() => void>(() => {});

  // Propriedades novas valem no próximo quadro, sem remontar o laço.
  useEffect(() => {
    settingsRef.current = settings;
    wakeRef.current();
  });

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const probe = document.createElement('canvas').getContext('2d');
    if (!container || !canvas || !ctx || !probe) return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /** Escala dos adereços: a referência foi desenhada para letras de 150px. */
    const scale = Math.min(1, Math.max(0.3, settingsRef.current.fontSize / 150));

    let width = 1;
    let height = 1;
    let dpr = 1;
    let raf = 0;
    let last = performance.now();
    let visible = true;
    let alive = true;
    let layoutKey = '';
    let requestedFont = '';
    let word: Layout | null = null;
    let glyphs: Glyph[] = [];
    let color: Rgb = [255, 255, 255];
    let accentColor: Rgb = [255, 255, 255];
    let clock = 0;
    let pulse = 0;
    let placed = false;
    const pointer = { x: 0, y: 0, inside: false };
    const lens = { x: 0, y: 0 };
    const frame = { x1: 0, y1: 0, x2: 0, y2: 0, alpha: 0, index: -1 };

    const refreshFonts = () => {
      layoutKey = '';
      wakeRef.current();
    };

    const family = () => getComputedStyle(container).fontFamily || 'sans-serif';
    const fontFor = (size: number) => `${settingsRef.current.fontWeight} ${size}px ${family()}`;

    const setFont = (target: CanvasRenderingContext2D, size: number) => {
      target.font = fontFor(size);
      if ('letterSpacing' in target) {
        target.letterSpacing = `${settingsRef.current.letterSpacing * size}px`;
      }
      target.textAlign = 'left';
      target.textBaseline = 'alphabetic';
    };

    /** Cada letra vira duas imagens prontas: a cheia e a tracejada. */
    const sprite = (view: Layout, glyph: Pick<Glyph, 'char' | 'x' | 'box'>, stroke: boolean) => {
      const s = settingsRef.current;
      const pad = Math.ceil(s.strokeWidth * 2 + 4);
      const left = glyph.box.x1 - pad;
      const top = glyph.box.y1 - pad;
      const w = glyph.box.x2 - glyph.box.x1 + pad * 2;
      const h = glyph.box.y2 - glyph.box.y1 + pad * 2;
      const image = document.createElement('canvas');
      image.width = Math.max(1, Math.ceil(w * dpr));
      image.height = Math.max(1, Math.ceil(h * dpr));
      const c = image.getContext('2d');
      if (!c) return { image, left, top };

      c.setTransform(dpr, 0, 0, dpr, -left * dpr, -top * dpr);
      setFont(c, view.size);
      if (stroke) {
        c.lineJoin = 'round';
        c.lineWidth = s.strokeWidth * 2;
        c.lineCap = 'butt';
        // A letra tracejada fica na cor do texto, como na referência; o laranja é do quadro.
        c.strokeStyle = rgba(color, 1);
        c.setLineDash([Math.max(1, s.dashLength), Math.max(1, s.dashGap)]);
        c.strokeText(glyph.char, glyph.x, view.baseline);
        c.setLineDash([]);
        // O miolo sai: do traço de largura dupla sobra só a metade de fora.
        c.globalCompositeOperation = 'destination-out';
        c.fillStyle = '#000000';
        c.fillText(glyph.char, glyph.x, view.baseline);
        c.globalCompositeOperation = 'source-over';
      } else {
        c.fillStyle = rgba(color, 1);
        c.fillText(glyph.char, glyph.x, view.baseline);
      }
      return { image, left, top };
    };

    /**
     * Mede a palavra e, se preciso, redimensiona o canvas para ela. A largura
     * é a do texto mais a folga; a altura é a do contêiner.
     */
    const ensureLayout = (): Layout | null => {
      const s = settingsRef.current;
      const estilo = getComputedStyle(container);
      color = toRgb(estilo.color);
      accentColor =
        s.accent === 'primary'
          ? toRgb(estilo.getPropertyValue('--color-primary').trim() || '#ff5a00')
          : color;
      const key = [
        s.text,
        family(),
        s.fontWeight,
        s.fontSize,
        s.letterSpacing,
        color.join(','),
        accentColor.join(','),
        s.dashLength,
        s.dashGap,
        s.strokeWidth,
        height,
        dpr,
      ].join('|');
      if (key === layoutKey && word) return word;

      const wanted = fontFor(64);
      if (wanted !== requestedFont) {
        requestedFont = wanted;
        document.fonts.load(wanted, s.text).then(refreshFonts, refreshFonts);
      }

      setFont(probe, s.fontSize);
      const m = probe.measureText(s.text);
      const inkWidth = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
      const inkHeight = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
      if (inkWidth < 1) return null;

      width = Math.ceil(inkWidth + PAD_X * 2);
      container.style.width = `${width}px`;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      layoutKey = key;

      const x = PAD_X + m.actualBoundingBoxLeft;
      const baseline = (height - inkHeight) / 2 + m.actualBoundingBoxAscent;
      const next: Layout = {
        size: s.fontSize,
        baseline,
        left: x - m.actualBoundingBoxLeft,
        right: x + m.actualBoundingBoxRight,
        top: baseline - m.actualBoundingBoxAscent,
        bottom: baseline + m.actualBoundingBoxDescent,
      };
      word = next;

      const previous = glyphs;
      glyphs = [];
      let prefix = '';
      for (const char of Array.from(s.text)) {
        prefix += char;
        const own = probe.measureText(char);
        const gx = x + probe.measureText(prefix).width - own.width;
        if (!char.trim()) continue;
        const base = {
          char,
          x: gx,
          box: {
            x1: gx - own.actualBoundingBoxLeft,
            y1: baseline - own.actualBoundingBoxAscent,
            x2: gx + own.actualBoundingBoxRight,
            y2: baseline + own.actualBoundingBoxDescent,
          },
        };
        const kept = previous[glyphs.length];
        glyphs.push({
          ...base,
          outline: kept?.char === char ? kept.outline : 0,
          fill: sprite(next, base, false),
          dashes: sprite(next, base, true),
        });
      }
      frame.index = -1;
      return next;
    };

    /** A letra mais próxima do ponto, na horizontal, até 28px de folga. */
    const glyphAt = (x: number, y: number): number => {
      if (!word || y < word.top - 24 || y > word.bottom + 24) return -1;
      let best = -1;
      let bestDistance = Infinity;
      glyphs.forEach((glyph, i) => {
        const d = x < glyph.box.x1 ? glyph.box.x1 - x : x > glyph.box.x2 ? x - glyph.box.x2 : 0;
        if (d < bestDistance) {
          bestDistance = d;
          best = i;
        }
      });
      return bestDistance < 28 ? best : -1;
    };

    const blit = (art: Sprite) => {
      ctx.drawImage(art.image, Math.round(art.left * dpr), Math.round(art.top * dpr));
    };

    const tick = (now: number) => {
      raf = 0;
      const s = settingsRef.current;
      const dt = Math.min(0.05, Math.max(0.001, (now - last) / 1000));
      last = now;
      const view = ensureLayout();
      if (!view) return;

      const sweeping = s.sweep && !reducedMotion && !pointer.inside;
      if (sweeping) clock += dt * s.speed;
      pulse += dt;

      let targetX = pointer.x;
      let targetY = pointer.y;
      if (sweeping) {
        targetX = view.left + (view.right - view.left) * (0.5 - 0.5 * Math.cos(clock * 0.45));
        targetY = view.top + (view.bottom - view.top) * (0.45 + 0.1 * Math.sin(clock * 0.8));
      }
      const active = pointer.inside || sweeping;
      if (active && !placed) {
        lens.x = targetX;
        lens.y = targetY;
      }
      if (active) {
        const lag = pointer.inside ? 0.05 : 0.22;
        lens.x = approach(lens.x, targetX, dt, lag);
        lens.y = approach(lens.y, targetY, dt, lag);
      }
      placed = active;

      const focus = active ? glyphAt(lens.x, lens.y) : -1;
      if (focus >= 0) {
        const glyph = glyphs[focus]!;
        const bx1 = glyph.box.x1 - 5;
        const by1 = glyph.box.y1 - 5;
        const bx2 = glyph.box.x2 + 5;
        const by2 = glyph.box.y2 + 5;
        if (frame.index < 0 || frame.alpha < 0.02) {
          frame.x1 = bx1;
          frame.y1 = by1;
          frame.x2 = bx2;
          frame.y2 = by2;
        }
        frame.x1 = approach(frame.x1, bx1, dt, 0.08);
        frame.y1 = approach(frame.y1, by1, dt, 0.08);
        frame.x2 = approach(frame.x2, bx2, dt, 0.08);
        frame.y2 = approach(frame.y2, by2, dt, 0.08);
        frame.index = focus;
      }
      frame.alpha = approach(frame.alpha, focus >= 0 ? 1 : 0, dt, 0.1);

      let settling = false;
      glyphs.forEach((glyph, i) => {
        const target = i === focus ? 1 : 0;
        glyph.outline = approach(glyph.outline, target, dt, 0.09);
        if (Math.abs(glyph.outline - target) > 0.002) settling = true;
        else glyph.outline = target;
      });

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const glyph of glyphs) {
        if (glyph.outline < 0.999) {
          ctx.globalAlpha = 1 - glyph.outline;
          blit(glyph.fill);
        }
        if (glyph.outline > 0.001) {
          ctx.globalAlpha = glyph.outline;
          blit(glyph.dashes);
        }
        ctx.globalAlpha = 1;
      }

      const glyph = glyphs[frame.index];
      if (glyph) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const label = s.labels
          ? `${glyph.char}  ${Math.round(glyph.box.x2 - glyph.box.x1)} × ${Math.round(
              glyph.box.y2 - glyph.box.y1,
            )}`
          : undefined;
        drawTechFrame(
          ctx,
          frame,
          frame.index + 1,
          pulse,
          frame.alpha,
          { accent: accentColor, specks: s.specks, scale, speed: s.speed, labelFont: LABEL_FONT },
          dpr,
          label,
        );
      }

      if (frame.alpha > 0.01 && frame.alpha < 0.99) settling = true;
      if ((active || settling) && visible && alive) raf = requestAnimationFrame(tick);
    };

    const wake = () => {
      if (raf || !visible || !alive) return;
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };
    wakeRef.current = wake;

    const resize = () => {
      height = Math.max(1, container.clientHeight);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      layoutKey = '';
      wake();
    };

    const locate = (event: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
    };
    const onMove = (event: PointerEvent) => {
      locate(event);
      pointer.inside = true;
      wake();
    };
    const onLeave = () => {
      pointer.inside = false;
      wake();
    };

    container.addEventListener('pointermove', onMove, { passive: true });
    container.addEventListener('pointerenter', onMove, { passive: true });
    container.addEventListener('pointerleave', onLeave, { passive: true });

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    // Trilho escondido (celular) ou fora da tela: o laço para.
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true;
      wake();
    });
    intersectionObserver.observe(container);
    document.fonts.ready.then(refreshFonts, refreshFonts);

    resize();

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      wakeRef.current = () => {};
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerenter', onMove);
      container.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={text}
      className={cn('relative h-full shrink-0 touch-pan-y select-none', className)}
    >
      <canvas ref={canvasRef} className="block" />
    </div>
  );
}
